import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  hexToRgb,
  rgbToHex,
  rgbToHsl,
  hslToRgb,
  generateHoneycombHexagons
} from './colorUtils';

export default function ColorPickerDialog({
  open,
  onClose,
  currentColor,
  onSelectColor
}) {
  const [activeTab, setActiveTab] = useState('standard'); // 'standard' | 'custom'
  const [selectedColor, setSelectedColor] = useState(currentColor || '#FFFFFF');
  
  // Custom Tab States
  const [hsl, setHsl] = useState({ h: 200, s: 100, l: 50 });
  const [rgb, setRgb] = useState({ r: 0, g: 128, b: 255 });
  const [hexInput, setHexInput] = useState('#0080FF');

  const [canvasEl, setCanvasEl] = useState(null);
  const sliderRef = useRef(null);
  const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);

  // Honeycomb hexagons
  const [hexagons, setHexagons] = useState([]);

  useEffect(() => {
    setHexagons(generateHoneycombHexagons(11));
  }, []);

  // Sync selectedColor state with parent's currentColor when dialog opens
  useEffect(() => {
    if (open) {
      const color = currentColor || '#FFFFFF';
      setSelectedColor(color);
      const parsedRgb = hexToRgb(color);
      const parsedHsl = rgbToHsl(parsedRgb);
      setRgb(parsedRgb);
      setHsl(parsedHsl);
      setHexInput(color.toUpperCase());
    }
  }, [open, currentColor]);

  // Redraw custom tab canvas when activeTab or canvas element is ready
  useEffect(() => {
    if (activeTab === 'custom' && canvasEl) {
      drawSpectrumCanvas(canvasEl);
    }
  }, [activeTab, canvasEl]);

  const drawSpectrumCanvas = (canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;

    // Draw 2D Spectrum: X = Hue (0 to 360), Y = Saturation (100% to 0%)
    for (let y = 0; y < height; y++) {
      const saturation = 100 - (y / height) * 100;
      const grad = ctx.createLinearGradient(0, y, width, y);
      
      // Add gradient stops for all Hues
      for (let i = 0; i <= 6; i++) {
        const h = (i / 6) * 360;
        grad.addColorStop(i / 6, `hsl(${h}, ${saturation}%, 50%)`);
      }
      
      ctx.fillStyle = grad;
      ctx.fillRect(0, y, width, 1);
    }
  };

  // Handle color change from Standard honeycomb
  const handleHoneycombClick = (color) => {
    setSelectedColor(color);
    const parsedRgb = hexToRgb(color);
    const parsedHsl = rgbToHsl(parsedRgb);
    setRgb(parsedRgb);
    setHsl(parsedHsl);
    setHexInput(color.toUpperCase());
  };

  // Handle custom canvas interactions
  const handleCanvasInteraction = (e) => {
    const canvas = canvasEl;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));

    const h = Math.round((x / rect.width) * 360);
    const s = Math.round(100 - (y / rect.height) * 100);
    
    // If lightness is black (0) or white (100), adjust it to 50%
    // so the selected color is actually visible.
    let l = hsl.l;
    if (l <= 5 || l >= 95) {
      l = 50;
    }

    const newHsl = { h, s, l };
    const newRgb = hslToRgb(newHsl);
    const newHex = rgbToHex(newRgb);

    setHsl(newHsl);
    setRgb(newRgb);
    setSelectedColor(newHex);
    setHexInput(newHex.toUpperCase());
  };

  // Handle vertical lightness slider interactions
  const handleSliderInteraction = (e) => {
    const slider = sliderRef.current;
    if (!slider) return;
    const rect = slider.getBoundingClientRect();
    const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));

    // Y = 0 (top) is 100% Lightness, Y = rect.height (bottom) is 0% Lightness
    const l = Math.round(100 - (y / rect.height) * 100);

    const newHsl = { ...hsl, l };
    const newRgb = hslToRgb(newHsl);
    const newHex = rgbToHex(newRgb);

    setHsl(newHsl);
    setRgb(newRgb);
    setSelectedColor(newHex);
    setHexInput(newHex.toUpperCase());
  };

  // Mouse drag handlers for Canvas
  const handleCanvasMouseDown = (e) => {
    setIsDraggingCanvas(true);
    handleCanvasInteraction(e);
  };

  // Mouse drag handlers for Slider
  const handleSliderMouseDown = (e) => {
    setIsDraggingSlider(true);
    handleSliderInteraction(e);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingCanvas) {
        handleCanvasInteraction(e);
      }
      if (isDraggingSlider) {
        handleSliderInteraction(e);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingCanvas(false);
      setIsDraggingSlider(false);
    };

    if (isDraggingCanvas || isDraggingSlider) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingCanvas, isDraggingSlider, hsl]);

  // Handle Input Changes
  const handleRgbInputChange = (key, value) => {
    const intVal = Math.max(0, Math.min(255, parseInt(value) || 0));
    const newRgb = { ...rgb, [key]: intVal };
    const newHsl = rgbToHsl(newRgb);
    const newHex = rgbToHex(newRgb);

    setRgb(newRgb);
    setHsl(newHsl);
    setSelectedColor(newHex);
    setHexInput(newHex.toUpperCase());
  };

  const handleHslInputChange = (key, value) => {
    const max = key === 'h' ? 360 : 100;
    const intVal = Math.max(0, Math.min(max, parseInt(value) || 0));
    const newHsl = { ...hsl, [key]: intVal };
    const newRgb = hslToRgb(newHsl);
    const newHex = rgbToHex(newRgb);

    setHsl(newHsl);
    setRgb(newRgb);
    setSelectedColor(newHex);
    setHexInput(newHex.toUpperCase());
  };

  const handleHexInputChange = (value) => {
    setHexInput(value);
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      const newRgb = hexToRgb(value);
      const newHsl = rgbToHsl(newRgb);
      setRgb(newRgb);
      setHsl(newHsl);
      setSelectedColor(value);
    }
  };

  const handleOk = () => {
    onSelectColor(selectedColor);
    onClose();
  };

  if (!open) return null;

  // Grayscale colors for standard tab
  const grayscaleRow = [
    { color: '#FFFFFF', x: -90 },
    { color: '#E0E0E0', x: -60 },
    { color: '#C0C0C0', x: -40 },
    { color: '#A0A0A0', x: -20 },
    { color: '#808080', x: 0 },
    { color: '#606060', x: 20 },
    { color: '#404040', x: 40 },
    { color: '#202020', x: 60 },
    { color: '#000000', x: 90 }
  ];

  // Helper to generate a flat-topped hexagon SVG path points
  const getHexPoints = (cx, cy, r) => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3;
      points.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
    }
    return points.join(' ');
  };

  return createPortal(
    <div className="windows-modal-backdrop">
      <div className="windows-dialog">
        {/* Title Bar */}
        <div className="windows-titlebar">
          <span style={{ fontSize: 13, fontWeight: 500 }}>Colors</span>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button className="windows-titlebar-btn close" onClick={onClose}>
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div style={{ display: 'flex', padding: '16px 14px', gap: 20, background: '#f3f3f3' }}>
          {/* Left Column (Picker area) */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #dcdcdc', marginBottom: 12 }}>
              <button
                className={`windows-tab ${activeTab === 'standard' ? 'active' : ''}`}
                onClick={() => setActiveTab('standard')}
              >
                Standard
              </button>
              <button
                className={`windows-tab ${activeTab === 'custom' ? 'active' : ''}`}
                onClick={() => setActiveTab('custom')}
              >
                Custom
              </button>
            </div>

            {/* Tab Contents */}
            <div style={{ background: '#ffffff', border: '1px solid #dcdcdc', borderRadius: 4, padding: 10, minHeight: 330, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {activeTab === 'standard' ? (
                <div>
                  <div style={{ fontSize: 11, marginBottom: 8, color: '#333', fontWeight: 500 }}>
                    <u>C</u>olors:
                  </div>
                  {/* Honeycomb SVG */}
                  <svg width="250" height="300" viewBox="-120 -125 240 290" style={{ display: 'block', margin: '0 auto' }}>
                    {/* Main Hexagonal Honeycomb */}
                    <g>
                      {hexagons.map((hex, idx) => (
                        <polygon
                          key={`hex-${idx}`}
                          points={getHexPoints(hex.x, hex.y, 10)}
                          fill={hex.color}
                          stroke={selectedColor.toUpperCase() === hex.color.toUpperCase() ? '#000000' : 'none'}
                          strokeWidth={1.5}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleHoneycombClick(hex.color)}
                        />
                      ))}
                    </g>

                    {/* Grayscale Honeycomb Row */}
                    <g transform="translate(0, 138)">
                      {grayscaleRow.map((gray, idx) => (
                        <polygon
                          key={`gray-${idx}`}
                          points={getHexPoints(gray.x, 0, 9)}
                          fill={gray.color}
                          stroke={selectedColor.toUpperCase() === gray.color.toUpperCase() ? '#0078D4' : '#E0E0E0'}
                          strokeWidth={selectedColor.toUpperCase() === gray.color.toUpperCase() ? 2 : 0.5}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleHoneycombClick(gray.color)}
                        />
                      ))}
                    </g>
                  </svg>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                  {/* Color Canvas */}
                  <div
                    style={{
                      position: 'relative',
                      width: 170,
                      height: 170,
                      border: '1px solid #888',
                      cursor: 'crosshair',
                      flexShrink: 0
                    }}
                    onMouseDown={handleCanvasMouseDown}
                  >
                    <canvas
                      ref={setCanvasEl}
                      width={170}
                      height={170}
                      style={{ display: 'block', width: '100%', height: '100%' }}
                    />
                    {/* Crosshair target indicator */}
                    <div
                      style={{
                        position: 'absolute',
                        left: `${(hsl.h / 360) * 170}px`,
                        top: `${((100 - hsl.s) / 100) * 170}px`,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        border: '1.5px solid #000',
                        boxShadow: '0 0 0 1px #fff',
                        transform: 'translate(-4px, -4px)',
                        pointerEvents: 'none'
                      }}
                    />
                  </div>

                  {/* Lightness Slider */}
                  <div
                    ref={sliderRef}
                    style={{
                      position: 'relative',
                      width: 18,
                      height: 170,
                      border: '1px solid #888',
                      cursor: 'ns-resize',
                      flexShrink: 0,
                      background: `linear-gradient(to bottom, #ffffff, hsl(${hsl.h}, ${hsl.s}%, 50%), #000000)`
                    }}
                    onMouseDown={handleSliderMouseDown}
                  >
                    {/* Slider pointer indicator (triangle arrows) */}
                    <div
                      style={{
                        position: 'absolute',
                        right: -10,
                        top: `${((100 - hsl.l) / 100) * 170}px`,
                        width: 0,
                        height: 0,
                        borderTop: '5px solid transparent',
                        borderBottom: '5px solid transparent',
                        borderRight: '7px solid #444',
                        transform: 'translateY(-5px)',
                        pointerEvents: 'none'
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        left: -10,
                        top: `${((100 - hsl.l) / 100) * 170}px`,
                        width: 0,
                        height: 0,
                        borderTop: '5px solid transparent',
                        borderBottom: '5px solid transparent',
                        borderLeft: '7px solid #444',
                        transform: 'translateY(-5px)',
                        pointerEvents: 'none'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column (Control Buttons + Preview + Input Values) */}
          <div style={{ width: 120, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="windows-btn primary" onClick={handleOk}>OK</button>
              <button className="windows-btn" onClick={onClose}>Cancel</button>
            </div>

            {/* Text Inputs (mainly for custom mode) */}
            {activeTab === 'custom' && (
              <div className="windows-inputs-group" style={{ margin: '10px 0' }}>
                <div className="windows-input-row">
                  <label><u>H</u>ue:</label>
                  <input
                    type="number"
                    value={hsl.h}
                    onChange={(e) => handleHslInputChange('h', e.target.value)}
                    min={0}
                    max={360}
                  />
                </div>
                <div className="windows-input-row">
                  <label><u>S</u>at:</label>
                  <input
                    type="number"
                    value={hsl.s}
                    onChange={(e) => handleHslInputChange('s', e.target.value)}
                    min={0}
                    max={100}
                  />
                </div>
                <div className="windows-input-row">
                  <label><u>L</u>um:</label>
                  <input
                    type="number"
                    value={hsl.l}
                    onChange={(e) => handleHslInputChange('l', e.target.value)}
                    min={0}
                    max={100}
                  />
                </div>
                <div style={{ height: 6 }} />
                <div className="windows-input-row">
                  <label>Red:</label>
                  <input
                    type="number"
                    value={rgb.r}
                    onChange={(e) => handleRgbInputChange('r', e.target.value)}
                    min={0}
                    max={255}
                  />
                </div>
                <div className="windows-input-row">
                  <label>Green:</label>
                  <input
                    type="number"
                    value={rgb.g}
                    onChange={(e) => handleRgbInputChange('g', e.target.value)}
                    min={0}
                    max={255}
                  />
                </div>
                <div className="windows-input-row">
                  <label>Blue:</label>
                  <input
                    type="number"
                    value={rgb.b}
                    onChange={(e) => handleRgbInputChange('b', e.target.value)}
                    min={0}
                    max={255}
                  />
                </div>
                <div className="windows-input-row hex">
                  <label>Hex:</label>
                  <input
                    type="text"
                    value={hexInput}
                    onChange={(e) => handleHexInputChange(e.target.value)}
                    maxLength={7}
                    style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
                  />
                </div>
              </div>
            )}

            {/* Preview Block */}
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: 11, color: '#333', marginBottom: 2 }}>New</div>
              <div
                style={{
                  width: 70,
                  height: 60,
                  border: '1px solid #7f7f7f',
                  background: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* Top Half: New Color */}
                <div style={{ flex: 1, backgroundColor: selectedColor }} />
                {/* Bottom Half: Current Color */}
                <div style={{ flex: 1, backgroundColor: currentColor || '#FFFFFF' }} />
              </div>
              <div style={{ fontSize: 11, color: '#333', marginTop: 2 }}>Current</div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
